import { useCallback, useMemo, useState, ReactNode } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle, ClipboardList, Download, ExternalLink, Loader2, Send, XCircle, Zap, ZapOff } from 'lucide-react'
import * as XLSX from 'xlsx'
import { ColumnDef } from '@tanstack/react-table'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import VirtualTable from '../components/VirtualTable'

// -- Data types --

interface InvoiceLineRow {
  id: string
  line_number: number | null
  description: string | null
  amount_net: string | null
  unit_price: string | null
  quantity: number | null
  vat_rate: string | null
  row_type: string | null
  cost_center: string | null
  cost_center_description: string | null
  trip_count: number
  bill_count: number
}

interface InvoiceRow {
  id: string
  access_code: string | null
  billing_group: string
  billing_ovt: string | null
  sub_organization_uuid: string | null
  period_start: string
  period_end: string
  net_amount: string | null
  trip_count: number
  bill_count: number
  status: string
  sent_at: string | null
  created_at: string
  lines: InvoiceLineRow[]
  subRows?: InvoiceTableRow[]
}

// Union type for TanStack Table (parent + child in same row model)
type InvoiceTableRow = InvoiceRow & Partial<InvoiceLineRow>

interface BillingInvoiceListResponse {
  data: InvoiceRow[]
  pagination: {
    page: number
    page_size: number
    total_items: number
    total_pages: number
  }
}

interface SendResult {
  access_code: string
  organization_name: string
  lines_count: number
  trip_details_count: number
  date_update_error?: string | null
}

interface SendBatchInvoicesResponse {
  invoices_sent: number
  invoices_failed: number
  errors: string[]
  warnings?: string[]
}

interface PublishBatchInvoicesResponse {
  invoices_published: number
  invoices_failed: number
  errors: string[]
}

interface PlusIdMode {
  enabled: boolean
  dry_run: boolean
  env: string
}

const INVOICE_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  ready: 'bg-blue-100 text-blue-800',
  sent: 'bg-orange-100 text-orange-800',
  published: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
}

// -- Formatters --

function formatDate(isoStr: string | null): string {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  return d.toLocaleDateString('fi-FI')
}

function formatCurrency(val: string | null): string {
  if (!val) return ''
  return Number(val).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' \u20AC'
}

export default function BillingInvoicesPage() {
  const { t } = useTranslation('billing')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const { billExportUuid } = useParams<{ billExportUuid: string }>()
  const [searchParams] = useSearchParams()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [warningMessage, setWarningMessage] = useState<string | null>(null)
  const [plusidLive, setPlusidLive] = useState(false)

  // Detect period mode: route param starts with _period_ prefix
  const isPeriodMode = billExportUuid?.startsWith('_period_') ?? false
  const periodId = isPeriodMode ? searchParams.get('invoice_period_id') : null

  const isOpenRedirect = billExportUuid === '_open'

  const exportUuidParam = isPeriodMode ? undefined : billExportUuid

  // PlusID mode (enabled/dry_run/env)
  const { data: plusidMode } = useQuery<PlusIdMode>({
    queryKey: ['plusidMode'],
    queryFn: () => apiClient.get('/billing/plusid-mode'),
    staleTime: 5 * 60 * 1000,
  })

  // Show toggle in dev/staging so user can choose dry-run vs live
  const showPlusidToggle = plusidMode?.enabled && plusidMode?.env !== 'production'

  // When toggle is hidden (production or plusid disabled), always live (no dry_run).
  // Only use the toggle state when it's actually visible to the user.
  const effectivePlusidLive = showPlusidToggle ? plusidLive : true

  // Fetch all invoices with lines (include_lines=true, large page_size)
  const { data, isLoading, error } = useQuery<BillingInvoiceListResponse>({
    queryKey: ['billingInvoices', exportUuidParam, periodId],
    enabled: !isOpenRedirect,
    queryFn: () => {
      const params = new URLSearchParams()
      params.set('page', '1')
      params.set('page_size', '1000')
      params.set('include_lines', 'true')
      if (periodId) {
        params.set('invoice_period_id', periodId)
      } else if (exportUuidParam) {
        params.set('bill_export_uuid', exportUuidParam)
      }
      return apiClient.get(`/billing/invoices?${params.toString()}`)
    },
  })

  const sendMutation = useMutation({
    mutationFn: (invoiceId: string) => {
      const params = effectivePlusidLive ? '' : '?dry_run=true'
      return apiClient.post<SendResult>(`/billing/invoices/${invoiceId}/send${params}`)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['billingInvoices'] })
      setSuccessMessage(
        t('invoices.sendSuccess', { accessCode: result.access_code })
      )
      setWarningMessage(
        result.date_update_error
          ? t('invoices.dateUpdateWarning', { error: result.date_update_error })
          : null
      )
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  // Batch send all invoices
  const sendBatchMutation = useMutation({
    mutationFn: () => {
      const params = effectivePlusidLive ? '' : '?dry_run=true'
      if (periodId) {
        return apiClient.post<SendBatchInvoicesResponse>(
          `/billing/invoice-periods/${periodId}/send-invoices${params}`
        )
      }
      return apiClient.post<SendBatchInvoicesResponse>(`/billing/send-batch-invoices${params}`, {
        bill_export_uuid: exportUuidParam || null,
        date_from: searchParams.get('date_from') || '',
        date_to: searchParams.get('date_to') || '',
      })
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['billingInvoices'] })
      if (result.invoices_failed > 0) {
        setSuccessMessage(
          t('bills.invoicesSentWithErrors', {
            sent: result.invoices_sent,
            failed: result.invoices_failed,
          })
        )
      } else {
        setSuccessMessage(
          t('bills.invoicesSent', { sent: result.invoices_sent })
        )
      }
      if (result.warnings && result.warnings.length > 0) {
        setWarningMessage(
          t('bills.dateUpdateWarnings', { count: result.warnings.length }) +
            '\n' +
            result.warnings.join('\n')
        )
      } else {
        setWarningMessage(null)
      }
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (invoiceId: string) => {
      const params = effectivePlusidLive ? '?plusid_live=true' : ''
      return apiClient.post(`/billing/invoices/${invoiceId}/revoke${params}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingInvoices'] })
      setSuccessMessage(t('invoices.revokeSuccess'))
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  // Batch publish all sent invoices to tenant organizations
  const publishBatchMutation = useMutation({
    mutationFn: () => {
      if (periodId) {
        return apiClient.post<PublishBatchInvoicesResponse>(
          `/billing/invoice-periods/${periodId}/publish-invoices`
        )
      }
      return apiClient.post<PublishBatchInvoicesResponse>('/billing/publish-batch-invoices', {
        bill_export_uuid: exportUuidParam || null,
        date_from: searchParams.get('date_from') || '',
        date_to: searchParams.get('date_to') || '',
      })
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['billingInvoices'] })
      if (result.invoices_failed > 0) {
        setSuccessMessage(
          t('invoices.publishedWithErrors', {
            published: result.invoices_published,
            failed: result.invoices_failed,
          })
        )
      } else {
        setSuccessMessage(
          t('invoices.publishSuccess', { published: result.invoices_published })
        )
      }
      setTimeout(() => setSuccessMessage(null), 5000)
    },
  })

  const handleSend = useCallback(
    (invoiceId: string) => {
      if (window.confirm(t('invoices.sendConfirm'))) {
        sendMutation.mutate(invoiceId)
      }
    },
    [t, sendMutation]
  )

  const handleRevoke = useCallback(
    (invoiceId: string) => {
      if (window.confirm(t('invoices.revokeConfirm'))) {
        revokeMutation.mutate(invoiceId)
      }
    },
    [t, revokeMutation]
  )

  // Parent columns
  const columns = useMemo<ColumnDef<InvoiceTableRow, unknown>[]>(
    () => [
      {
        id: 'billing_group',
        accessorKey: 'billing_group',
        header: t('invoices.billingGroup'),
        filterFn: 'includesString',
        size: 200,
      },
      {
        id: 'billing_ovt',
        accessorKey: 'billing_ovt',
        header: t('invoices.billingOvt'),
        cell: ({ getValue }) => (getValue() as string) || '\u2014',
        filterFn: 'includesString',
        size: 120,
      },
      {
        id: 'period',
        accessorFn: (row) => `${row.period_start || ''} ${row.period_end || ''}`,
        header: t('invoices.period'),
        cell: ({ row }) =>
          `${formatDate(row.original.period_start)} \u2013 ${formatDate(row.original.period_end)}`,
        filterFn: 'includesString',
        size: 160,
      },
      {
        id: 'net_amount',
        accessorKey: 'net_amount',
        header: t('invoices.netAmount'),
        cell: ({ getValue }) => (
          <span className="font-mono">
            {formatCurrency(getValue() as string | null)}
          </span>
        ),
        filterFn: 'includesString',
        size: 120,
      },
      {
        id: 'bill_count',
        accessorKey: 'bill_count',
        header: t('invoices.billCount'),
        filterFn: 'includesString',
        size: 80,
      },
      {
        id: 'trip_count',
        accessorKey: 'trip_count',
        header: t('invoices.tripCount'),
        filterFn: 'includesString',
        size: 80,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('runs.status'),
        cell: ({ getValue }) => {
          const status = getValue() as string
          return (
            <span
              className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                INVOICE_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {t(`invoices.${status}`)}
            </span>
          )
        },
        filterFn: 'includesString',
        size: 100,
      },
      {
        id: 'access_code',
        accessorKey: 'access_code',
        header: t('invoices.accessCode'),
        cell: ({ getValue }) => (
          <span className="font-mono">{(getValue() as string) || '\u2014'}</span>
        ),
        filterFn: 'includesString',
        size: 100,
      },
      {
        id: 'actions',
        header: '',
        enableColumnFilter: false,
        cell: ({ row }): ReactNode => {
          const inv = row.original
          if (row.depth > 0) return null
          return (
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              {!['sent', 'published', 'cancelled'].includes(inv.status) && (
                <button
                  onClick={() => handleSend(inv.id)}
                  disabled={sendMutation.isPending}
                  className="inline-flex items-center px-2 py-0.5 text-[10px] bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                  title={t('invoices.send')}
                >
                  <Send className="w-3 h-3 mr-1" />
                  {t('invoices.send')}
                </button>
              )}
              {(inv.status === 'sent' || inv.status === 'published') && (
                <>
                  <button
                    onClick={() => handleRevoke(inv.id)}
                    disabled={revokeMutation.isPending}
                    className="inline-flex items-center px-2 py-0.5 text-[10px] bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-50"
                    title={t('invoices.revoke')}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    {t('invoices.revoke')}
                  </button>
                  {inv.sent_at && (
                    <span className="text-[10px] text-gray-500">
                      {formatDate(inv.sent_at)}
                    </span>
                  )}
                </>
              )}
            </div>
          )
        },
        size: 140,
      },
    ],
    [t, handleSend, handleRevoke, sendMutation.isPending, revokeMutation.isPending]
  )

  // Sub-columns (invoice lines, 8 columns)
  const subColumns = useMemo(
    () => [
      {
        key: 'line_number',
        header: '#',
      },
      {
        key: 'description',
        header: t('invoices.lineDescription'),
      },
      {
        key: 'cost_center',
        header: t('accounting.costCenter'),
      },
      {
        key: 'cost_center_description',
        header: t('accounting.description'),
      },
      {
        key: 'row_type',
        header: t('invoices.lineType'),
        render: (val: unknown) => {
          if (val === 'trip') return t('invoices.lineTrip')
          if (val === 'service') return t('invoices.lineService')
          if (val === 'credit') return t('invoices.lineCredit')
          if (val === 'correction') return t('invoices.lineCorrection')
          return String(val || '')
        },
      },
      {
        key: 'bill_count',
        header: t('invoices.billCount'),
        align: 'right' as const,
      },
      {
        key: 'trip_count',
        header: t('invoices.tripCount'),
        align: 'right' as const,
      },
      {
        key: 'amount_net',
        header: t('invoices.netAmount'),
        align: 'right' as const,
        render: (val: unknown) => formatCurrency(val as string | null),
      },
      {
        key: 'vat_rate',
        header: t('invoices.vatRate'),
        align: 'right' as const,
        render: (val: unknown) => (val ? `${val} %` : ''),
      },
    ],
    [t]
  )

  // Transform data: add subRows from lines
  const tableData = useMemo<InvoiceTableRow[]>(() => {
    if (!data?.data) return []
    return data.data.map((inv) => ({
      ...inv,
      subRows: (inv.lines || []) as unknown as InvoiceTableRow[],
    }))
  }, [data])

  const hasUnsent = tableData.some((inv) => !['sent', 'published'].includes(inv.status))
  const hasSent = tableData.some((inv) => inv.status === 'sent')
  const allPublished = tableData.length > 0 && tableData.every((inv) => inv.status === 'published')

  const handleExport = useCallback(
    (filteredParents: InvoiceTableRow[]) => {
      if (!filteredParents.length) return

      const parentHeaders = [
        t('invoices.billingGroup'), t('invoices.billingOvt'),
        t('invoices.period') + ' alku', t('invoices.period') + ' loppu',
        t('invoices.netAmount'), t('invoices.billCount'),
        t('invoices.tripCount'), t('runs.status'), t('invoices.accessCode'),
      ]
      const childHeaders = [
        '', '#', t('invoices.lineDescription'), t('accounting.costCenter'),
        t('accounting.description'), t('invoices.lineType'),
        t('invoices.billCount'), t('invoices.tripCount'),
        t('invoices.netAmount'), t('invoices.vatRate'),
      ]
      const maxWidth = Math.max(parentHeaders.length, childHeaders.length)
      const rows: unknown[][] = [parentHeaders]

      for (const inv of filteredParents) {
        rows.push([
          inv.billing_group, inv.billing_ovt || '',
          inv.period_start ? formatDate(inv.period_start) : '',
          inv.period_end ? formatDate(inv.period_end) : '',
          inv.net_amount ? Number(inv.net_amount) : '',
          inv.bill_count, inv.trip_count,
          inv.status, inv.access_code || '',
        ])
        // Child rows (invoice lines)
        const lines = (inv as InvoiceRow).lines || []
        for (const line of lines) {
          const cRow: unknown[] = [
            '  ↳', line.line_number, line.description || '',
            line.cost_center || '', line.cost_center_description || '',
            line.row_type || '', line.bill_count, line.trip_count,
            line.amount_net ? Number(line.amount_net) : '',
            line.vat_rate ? `${line.vat_rate} %` : '',
          ]
          while (cRow.length < maxWidth) cRow.push('')
          rows.push(cRow)
        }
      }

      const ws = XLSX.utils.aoa_to_sheet(rows)
      ws['!cols'] = [
        { wch: 30 }, { wch: 16 }, { wch: 12 }, { wch: 12 },
        { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
      ]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Laskut')
      const today = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(wb, `laskut_${today}.xlsx`)
    },
    [t]
  )

  const renderToolbarExtra = useCallback(
    (filteredParents: InvoiceTableRow[]) => (
      <button
        onClick={() => handleExport(filteredParents)}
        disabled={!filteredParents.length}
        className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-green-50 text-green-700 border-green-200 disabled:opacity-50"
      >
        <Download className="w-3 h-3" />
        {t('actions.export')}
      </button>
    ),
    [t, handleExport]
  )

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
      </div>
    )
  }

  // Open bills don't have batch-level invoices — redirect back
  if (isOpenRedirect) {
    return <Navigate to="/payment-traffic" replace />
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4 shrink-0">
        <Link
          to="/payment-traffic"
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">
            {t('invoices.title')}
          </h1>
          <p className="text-sm text-gray-500">{t('invoices.subtitle')}</p>
        </div>
        {/* PlusID mode toggle (staging/dev only) */}
        {showPlusidToggle && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPlusidLive(!plusidLive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                plusidLive ? 'bg-amber-500' : 'bg-gray-300'
              }`}
              title={plusidLive ? t('plusid.liveMode') : t('plusid.dryRunMode')}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  plusidLive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`inline-flex items-center gap-1 text-xs font-medium ${
              plusidLive ? 'text-amber-700' : 'text-gray-500'
            }`}>
              {plusidLive ? (
                <><Zap className="w-3 h-3" />{t('plusid.live')}</>
              ) : (
                <><ZapOff className="w-3 h-3" />{t('plusid.dryRun')}</>
              )}
            </span>
          </div>
        )}
        {/* PlusID disabled indicator */}
        {plusidMode && !plusidMode.enabled && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
            <ZapOff className="w-3 h-3" />
            {t('plusid.disabled')}
          </span>
        )}
        {/* Batch send button */}
        {hasUnsent && (
          <button
            onClick={() => {
              if (window.confirm(t('invoices.sendConfirm'))) {
                sendBatchMutation.mutate()
              }
            }}
            disabled={sendBatchMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
          >
            {sendBatchMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {t('bills.sendInvoices')}
          </button>
        )}
        {/* Batch publish button — shown when any invoice is still in sent status */}
        {hasSent && !hasUnsent && (
          <button
            onClick={() => {
              if (window.confirm(t('invoices.publishConfirm'))) {
                publishBatchMutation.mutate()
              }
            }}
            disabled={publishBatchMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
          >
            {publishBatchMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ClipboardList className="w-4 h-4 mr-2" />
            )}
            {t('invoices.publishBatch')}
          </button>
        )}
        {allPublished && (
          <span className="inline-flex items-center text-sm text-green-600">
            <CheckCircle className="w-4 h-4 mr-1" />
            {t('invoices.batchPublished')}
          </span>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-green-800 text-sm mb-4 shrink-0">
          {successMessage}
        </div>
      )}

      {/* Warning Message (sent OK, but PlusID date update failed) */}
      {warningMessage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-amber-800 text-sm mb-4 shrink-0 whitespace-pre-line">
          {warningMessage}
        </div>
      )}

      {/* Error Message */}
      {(sendMutation.isError || sendBatchMutation.isError || revokeMutation.isError || publishBatchMutation.isError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800 text-sm mb-4 shrink-0">
          {revokeMutation.isError ? t('invoices.revokeError') : publishBatchMutation.isError ? t('invoices.publishError') : t('invoices.sendError')}
        </div>
      )}

      {/* Table */}
      <VirtualTable<InvoiceTableRow, InvoiceLineRow>
        data={tableData}
        columns={columns}
        getSubRows={(row) => row.subRows}
        subColumns={subColumns}
        asChild={(row) => row as unknown as InvoiceLineRow}
        estimateSize={28}
        overscan={30}
        filterPlaceholder={t('table.filterPlaceholder')}
        globalFilterPlaceholder={t('table.globalSearch')}
        expandAllLabel={t('materials.expandAll')}
        collapseAllLabel={t('materials.collapseAll')}
        rowCountLabel={t('invoices.invoiceCount')}
        subRowCountLabel={t('invoices.lineCount')}
        renderToolbarExtra={renderToolbarExtra}
        contextMenuItems={(row: InvoiceTableRow) => {
          if (!row.access_code || row.status !== 'published') return []
          return [{
            label: t('materials.openInvoice'),
            icon: <ExternalLink className="w-3 h-3" />,
            onClick: () => window.open(`/invoices/access/${row.access_code}`, '_blank'),
          }]
        }}
      />
    </div>
  )
}
